/* File: headerMenuButtons.jsx
 * Path: ananunadm/src/components/main/headerMenuButtons.jsx
 */
import { useMemo, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
	Button,
	Tooltip,
	Popover,
	NavLink,
	Image,
	Stack,
	Group,
	Text,
	Menu,
	useMantineColorScheme
} from "@mantine/core";
import { IconApps } from "@tabler/icons-react";
import config from "@/config";
import { useLang } from "@/helpers/language";
import { useUser } from "@/helpers/userContext";
import { filterMenuItemsByReadPermission } from "@/helpers/platformMenuAccess";
import { withoutPageSettingsItems } from "@/helpers/pageSettingsNavigation";
import { T } from "@/helpers/translator";

function getMenuAssetSrc(icon) {
	const assetPath = `/assets/${icon}`;
	return config.api.MAIN_APP_URL ? `${config.api.MAIN_APP_URL}${assetPath}` : assetPath;
}

/** Prefer active locale, then English, then any remaining string value. */
function localizedText(value, lang, fallback = "") {
	if (value == null) return fallback;
	if (typeof value === "string") return value;
	if (typeof value !== "object") return fallback;
	const picked = value[lang] || value.en || value.hy || value.ru;
	if (typeof picked === "string" && picked.trim()) return picked;
	const first = Object.values(value).find((entry) => typeof entry === "string" && entry.trim());
	return first || fallback;
}

function capitalizeLabel(raw, fallback = "") {
	if (!raw || typeof raw !== "string") return fallback;
	return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function pathMatchesRoute(pathname, route) {
	const norm = pathname.replace(/\/$/, "") || "/";
	const expected = `/${route}`.replace(/\/$/, "");
	return norm === expected || norm.startsWith(`${expected}/`);
}

const POPOVER_MIDDLEWARES = { flip: false, shift: true, inline: false };

export default function HeaderMenuButtons({ isMobile, onAiAssistantOpen }) {
	const history = useHistory();
	const location = useLocation();
	const { lang } = useLang();
	const { user } = useUser();
	const { colorScheme } = useMantineColorScheme();

	const [openRoute, setOpenRoute] = useState(null);

	const platforms = useMemo(() => config.headerMenu || [], []);

	const closePopover = useCallback(() => setOpenRoute(null), []);

	const go = useCallback(
		(route, platformRoute = "") => {
			if (platformRoute === "ai" && onAiAssistantOpen)
			{
				onAiAssistantOpen();
				closePopover();
				return;
			}
			history.push(`/${route}`);
			closePopover();
		},
		[history, closePopover, onAiAssistantOpen]
	);

	const iconMx = isMobile ? 1 : 2;

	const visiblePlatforms = useMemo(() => {
		return platforms
			.map((platform) => ({
				platform,
				filteredItems: withoutPageSettingsItems(
					filterMenuItemsByReadPermission(platform.menu || [], user)
				)
			}))
			.filter(({ filteredItems }) => filteredItems.length > 0);
	}, [platforms, user]);

	if (isMobile)
	{
		return (
			<Menu
				shadow="md"
				width={260}
				position="bottom-end"
				withArrow
				withinPortal
				middlewares={POPOVER_MIDDLEWARES}
			>
				<Menu.Target>
					<Button
						variant="subtle"
						size="md"
						aria-label={T("appsMenu", lang)}
						style={{ flexShrink: 0, margin: 0, padding: 10 }}
					>
						<IconApps size={20} stroke={1.5} />
					</Button>
				</Menu.Target>
				<Menu.Dropdown>
					{visiblePlatforms.map(({ platform, filteredItems }) => {
						const label = localizedText(platform.title, lang, platform.route);
						const iconSrc = platform.icon?.includes("/")
							? platform.icon
							: `/assets/${platform.icon}`;
						const showGroupHeader = platform.route === "system";
						return (
							<div key={platform.id}>
								{showGroupHeader ? (
									<Menu.Label>
										<Group spacing={6} noWrap>
											<Image src={iconSrc} alt="" width={16} height={16} />
											<Text size="xs" weight={600} transform="uppercase">
												{label}
											</Text>
										</Group>
									</Menu.Label>
								) : null}
								{filteredItems.map((item) => {
									const navLabel = capitalizeLabel(
										localizedText(item.name, lang, item.route),
										item.route
									);
									return (
										<Menu.Item
											key={item.id || item.route}
											icon={
												<Image
													src={getMenuAssetSrc(item.icon)}
													alt=""
													width={20}
													height={20}
													style={{
														filter:
															colorScheme === "dark" ? "invert(1)" : undefined
													}}
												/>
											}
											onClick={() => go(item.route, platform.route)}
										>
											{navLabel}
										</Menu.Item>
									);
								})}
								{showGroupHeader ? <Menu.Divider /> : null}
							</div>
						);
					})}
				</Menu.Dropdown>
			</Menu>
		);
	}

	return (
		<Group spacing={6} noWrap style={{ flexShrink: 0 }}>
			{platforms.map((platform) => {
				const filteredItems = withoutPageSettingsItems(
					filterMenuItemsByReadPermission(platform.menu || [], user)
				);
				if (filteredItems.length === 0)
				{
					return null;
				}

				const label = localizedText(platform.title, lang, platform.route);
				const active =
					pathMatchesRoute(location.pathname, platform.route) ||
					filteredItems.some((item) => pathMatchesRoute(location.pathname, item.route));
				const iconSrc = platform.icon?.includes("/")
					? platform.icon
					: `/assets/${platform.icon}`;

				const popoverOpen = openRoute === platform.route;
				const popoverTitle = label;
				const opensAssistantDirectly = platform.route === "ai" && onAiAssistantOpen;
				const navigatesDirectly =
					platform.route === "calendar" || platform.route === "world-map";
				const isDirectAction = opensAssistantDirectly || navigatesDirectly;

				const actionIcon = (
					<Button
						variant={active ? "light" : "subtle"}
						size="md"
						onClick={() => {
							if (opensAssistantDirectly)
							{
								onAiAssistantOpen();
								return;
							}
							if (navigatesDirectly)
							{
								history.push(`/${platform.route}`);
								return;
							}
							setOpenRoute((prev) =>
								prev === platform.route ? null : platform.route
							);
						}}
						aria-label={label}
						aria-expanded={isDirectAction ? undefined : popoverOpen}
						aria-haspopup={isDirectAction ? undefined : "menu"}
						style={{
							cursor: "pointer",
							position: "relative",
							overflow: "visible",
							margin: 0,
							padding: 10,
						}}
						mx={iconMx}
					>
						<Image
							src={iconSrc}
							alt=""
							width={20}
							height={20}
						/>
					</Button>
				);

				if (isDirectAction)
				{
					return (
						<span key={platform.id} style={{ display: "inline-flex" }}>
							<Tooltip
								label={label}
								position="bottom"
								withinPortal
								events={{ hover: true, focus: true, touch: false }}
							>
								{actionIcon}
							</Tooltip>
						</span>
					);
				}

				return (
					<span key={platform.id} style={{ display: "inline-flex" }}>
						<Popover
							position="bottom"
							withArrow
							shadow="md"
							withinPortal
							middlewares={POPOVER_MIDDLEWARES}
							opened={popoverOpen}
							onChange={(opened) => {
								setOpenRoute(opened ? platform.route : null);
							}}
						>
							<Popover.Target>
								<Tooltip
									label={label}
									position="bottom"
									withinPortal
									events={{ hover: true, focus: true, touch: false }}
								>
									{actionIcon}
								</Tooltip>
							</Popover.Target>
							<Popover.Dropdown p="sm">
								<Text size="sm" weight={600} mb="xs">
									{popoverTitle}
								</Text>
								<Stack spacing={4}>
									{filteredItems.map((item) => {
										const navLabel = capitalizeLabel(
											localizedText(item.name, lang, item.route),
											item.route
										);
										return (
											<NavLink
												key={item.id || item.route}
												label={navLabel}
												icon={
													<Image
														src={getMenuAssetSrc(item.icon)}
														alt=""
														width={28}
														height={28}
														style={{
															filter:
																colorScheme === "dark"
																	? "invert(1)"
																	: undefined
														}}
													/>
												}
												onClick={() => go(item.route, platform.route)}
												active={pathMatchesRoute(location.pathname, item.route)}
												variant="subtle"
											/>
										);
									})}
								</Stack>
							</Popover.Dropdown>
						</Popover>
					</span>
				);
			})}
		</Group>
	);
}
